/* 
 * Component: Collection Grid Page
 * Description: Advanced product grid with filtering, search, and sorting functionality
 */

// Global collection products data (previously inline script)
var collectionProducts = {
  'all': []
};

class CollectionGridPage {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    
    // Cache DOM elements
    this.productCards = element.querySelectorAll('.product-card');
    this.collectionLinks = element.querySelectorAll('.collection-filter-link');
    this.searchInput = element.querySelector('.product-search-input');
    this.searchClear = element.querySelector('.search-clear');
    this.filterPanel = element.querySelector('.filter-dropdown-panel');
    this.filterToggle = element.querySelector('.filter-toggle-btn');
    
    // Search state
    this.searchTimeout = null;

    // Pill filter state
    this.activePillTag = '';
    this.pillIsAll = true;
    this.noResultsEl = null;

    this.init();
  }

  init() {
    this.initImageSliders();
    this.initColorSwatches();
    this.initCollectionFilters();
    this.initSorting();
    this.initSearch();
    this.initFilterPanel();
    this.initFullCardClickable();
    this.initPillFilterListener();
  }

  initImageSliders() {
    const sliders = this.element.querySelectorAll('.product-slider');
    
    sliders.forEach(slider => {
      const wrapper = slider.querySelector('.product-slider-wrapper');
      const slides = slider.querySelectorAll('.product-slide');
      const card = slider.closest('.product-card');
      const dots = card.querySelectorAll('.dot');
      
      if (slides.length <= 1) return;
      
      let currentSlide = 0;
      const slideCount = slides.length;
      
      this.updateDots(dots, currentSlide);
      
      // Touch swipe functionality
      let touchStartX = 0;
      let touchEndX = 0;
      
      slider.addEventListener('touchstart', e => {
        touchStartX = e.changedTouches[0].screenX;
      }, {passive: true});
      
      slider.addEventListener('touchend', e => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX, currentSlide, slideCount, wrapper, dots, (newSlide) => {
          currentSlide = newSlide;
        });
      }, {passive: true});
      
      // Mouse drag functionality
      this.initMouseDrag(slider, wrapper, dots, currentSlide, slideCount, (newSlide) => {
        currentSlide = newSlide;
      });
      
      // Connect dots to slides
      if (dots && dots.length) {
        dots.forEach((dot, i) => {
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            currentSlide = i;
            this.updateSlider(wrapper, currentSlide);
            this.updateDots(dots, currentSlide);
          });
        });
      }
    });
  }

  initMouseDrag(slider, wrapper, dots, currentSlide, slideCount, setCurrentSlide) {
    let isDragging = false;
    let startPosX = 0;
    
    slider.addEventListener('mousedown', e => {
      e.preventDefault();
      isDragging = true;
      startPosX = e.clientX;
      slider.style.cursor = 'grabbing';
    });
    
    slider.addEventListener('mousemove', e => {
      if (!isDragging) return;
      e.preventDefault();
      const moveX = e.clientX - startPosX;
      const slideWidth = slider.offsetWidth;
      
      if (Math.abs(moveX) < slideWidth * 0.5) {
        wrapper.style.transform = `translateX(${-currentSlide * 100 + (moveX / slideWidth * 100)}%)`;
      }
    });
    
    const handleMouseUp = (e) => {
      if (!isDragging) return;
      
      isDragging = false;
      slider.style.cursor = 'grab';
      
      const moveX = e.clientX - startPosX;
      const threshold = slider.offsetWidth * 0.2;
      
      if (moveX < -threshold) {
        currentSlide = Math.min(currentSlide + 1, slideCount - 1);
      } else if (moveX > threshold) {
        currentSlide = Math.max(currentSlide - 1, 0);
      }
      
      setCurrentSlide(currentSlide);
      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    };
    
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        slider.style.cursor = 'grab';
        this.updateSlider(wrapper, currentSlide);
      }
    });
  }

  handleSwipe(startX, endX, currentSlide, slideCount, wrapper, dots, setCurrentSlide) {
    const threshold = 50;
    
    if (startX - endX > threshold) {
      currentSlide = Math.min(currentSlide + 1, slideCount - 1);
      setCurrentSlide(currentSlide);
      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    } else if (endX - startX > threshold) {
      currentSlide = Math.max(currentSlide - 1, 0);
      setCurrentSlide(currentSlide);
      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    }
  }

  updateSlider(wrapper, currentSlide) {
    wrapper.style.transform = `translateX(-${currentSlide * 100}%)`;
  }

  updateDots(dots, currentSlide) {
    if (dots && dots.length) {
      dots.forEach((dot, i) => {
        if (i === currentSlide) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
  }

  initColorSwatches() {
    const swatches = this.element.querySelectorAll('.color-swatch');
    
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const productCard = swatch.closest('.product-card');
        const variantImage = swatch.getAttribute('data-variant-image');
        const cardSwatches = productCard.querySelectorAll('.color-swatch');
        
        // Remove active class from all swatches in this product
        cardSwatches.forEach(s => s.classList.remove('active'));
        
        // Add active class to clicked swatch
        swatch.classList.add('active');
        
        // Change product image if variant has an image
        if (variantImage && variantImage !== '') {
          const productImages = productCard.querySelectorAll('.product-image');
          if (productImages.length > 0) {
            productImages[0].src = variantImage;
          }
        }
      });
    });
  }

  initCollectionFilters() {
    // Handle initial state based on URL collection or active link
    const activeLink = this.element.querySelector('.collection-filter-link.active');
    if (activeLink) {
      const activeCollection = activeLink.getAttribute('data-collection');
      
      if (activeCollection === 'all') {
        // Show all products when "Shop All" is active
        this.productCards.forEach(card => {
          card.style.display = '';
        });
      } else {
        // Filter products by specific collection
        this.productCards.forEach(card => {
          const cardCollections = card.getAttribute('data-collection') || '';
          if (cardCollections.includes(activeCollection)) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
      }
    } else {
      // No active link, show all products
      this.productCards.forEach(card => {
        card.style.display = '';
      });
    }
    
    this.collectionLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Remove active class from all links
        this.collectionLinks.forEach(l => l.classList.remove('active'));
        
        // Add active class to clicked link
        link.classList.add('active');
        
        const collectionHandle = link.getAttribute('data-collection');
        
        // Show/hide product cards based on collection
        this.productCards.forEach(card => {
          const cardCollections = card.getAttribute('data-collection') || '';
          
          let shouldShow = false;
          
          if (collectionHandle === 'all') {
            shouldShow = true;
          } else {
            shouldShow = cardCollections.includes(collectionHandle);
          }
          
          if (shouldShow) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });

        // Clear search when switching collections
        if (this.searchInput && this.searchInput.value.trim() !== '') {
          this.searchInput.value = '';
          if (this.searchClear) this.searchClear.style.display = 'none';
          this.element.classList.remove('searching');
        }
      });
    });
  }

  initSorting() {
    const sortInputs = this.element.querySelectorAll('.sf-sort-input');
    if (!sortInputs.length) return;

    sortInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.sortProducts(input.value);
      });
    });
  }

  sortProducts(sortValue) {
    const productGrid = this.element.querySelector('.product-grid');
    if (!productGrid) return;

    const visibleProducts = Array.from(this.productCards).filter(card => card.style.display !== 'none');

    visibleProducts.sort((a, b) => {
      switch (sortValue) {
        case 'price-ascending':
          return parseFloat(a.getAttribute('data-price')) - parseFloat(b.getAttribute('data-price'));
        case 'price-descending':
          return parseFloat(b.getAttribute('data-price')) - parseFloat(a.getAttribute('data-price'));
        case 'created-descending':
          return new Date(b.getAttribute('data-created')) - new Date(a.getAttribute('data-created'));
        case 'in-stock': {
          const aStock = a.getAttribute('data-available') === 'true' ? 0 : 1;
          const bStock = b.getAttribute('data-available') === 'true' ? 0 : 1;
          return aStock - bStock;
        }
        default:
          return parseInt(a.getAttribute('data-index')) - parseInt(b.getAttribute('data-index'));
      }
    });

    visibleProducts.forEach(product => {
      productGrid.appendChild(product);
    });
  }

  initSearch() {
    if (!this.searchInput) return;
    
    this.searchInput.addEventListener('input', (e) => {
      const searchTerm = e.target.value.toLowerCase().trim();
      
      // Show/hide clear button
      if (searchTerm.length > 0) {
        if (this.searchClear) this.searchClear.style.display = 'flex';
        this.element.classList.add('searching');
      } else {
        if (this.searchClear) this.searchClear.style.display = 'none';
        this.element.classList.remove('searching');
      }
      
      // Debounce search for better performance
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.performSearch(searchTerm);
      }, 300);
    });
    
    // Clear search handler
    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchClear.style.display = 'none';
        this.element.classList.remove('searching');
        this.showAllProducts();
      });
    }
  }

  performSearch(searchTerm) {
    if (searchTerm === '') {
      this.showAllProducts();
      return;
    }

    this.productCards.forEach(card => {
      // Respect active pill filter
      if (!this.cardMatchesPill(card)) {
        card.classList.add('hidden');
        card.style.display = 'none';
        return;
      }

      const title = card.getAttribute('data-title')?.toLowerCase() || '';
      const vendor = card.getAttribute('data-vendor')?.toLowerCase() || '';
      const tags = card.getAttribute('data-tags')?.toLowerCase() || '';
      const type = card.getAttribute('data-type')?.toLowerCase() || '';

      const isMatch = title.includes(searchTerm) ||
                     vendor.includes(searchTerm) ||
                     tags.includes(searchTerm) ||
                     type.includes(searchTerm);

      if (isMatch) {
        card.classList.remove('hidden');
        card.style.display = '';
      } else {
        card.classList.add('hidden');
        setTimeout(() => {
          if (card.classList.contains('hidden')) {
            card.style.display = 'none';
          }
        }, 300);
      }
    });
  }

  showAllProducts() {
    this.productCards.forEach(card => {
      card.classList.remove('hidden');
      if (this.cardMatchesPill(card)) {
        card.style.display = '';
      } else {
        card.style.display = 'none';
      }
    });
  }

  initFilterPanel() {
    if (!this.filterPanel) return;

    const clearBtn = this.filterPanel.querySelector('[data-clear-filters]');
    const inputs = this.filterPanel.querySelectorAll('.sf-input:not(.sf-sort-input)');
    const sortInputs = this.filterPanel.querySelectorAll('.sf-sort-input');
    const closeBtn = this.filterPanel.querySelector('.filter-dropdown-panel__close');

    // Toggle panel visibility
    const togglePanel = (show) => {
      if (show === undefined) {
        show = this.filterPanel.style.display === 'none';
      }
      this.filterPanel.style.display = show ? 'block' : 'none';
      if (this.filterToggle) {
        this.filterToggle.setAttribute('aria-expanded', show ? 'true' : 'false');
      }
    };

    // Toggle button click
    if (this.filterToggle) {
      this.filterToggle.addEventListener('click', () => togglePanel());
    }

    // Close button click
    if (closeBtn) {
      closeBtn.addEventListener('click', () => togglePanel(false));
    }

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (this.filterPanel.style.display !== 'none' &&
          !this.filterPanel.contains(e.target) &&
          !this.filterToggle?.contains(e.target)) {
        togglePanel(false);
      }
    });

    // Filter functionality
    const getActiveFilters = () => {
      const groups = {};
      inputs.forEach(input => {
        if (input.checked) {
          (groups[input.dataset.filterGroup] ||= []).push(input.value);
        }
      });
      return groups;
    };

    const matchesFilters = (card, groups) => {
      // Pill filter is a prerequisite
      if (!this.cardMatchesPill(card)) return false;

      const vendor = this.normalize(card.dataset.vendor);
      const tags = (card.dataset.tags || '').toLowerCase();
      const title = this.normalize(card.dataset.title);
      const type = this.normalize(card.dataset.type);
      const cardCollections = card.getAttribute('data-collection') || '';

      // Supplement-specific data attributes
      const bestFor = (card.dataset.bestFor || '').toLowerCase();
      const stimLevel = (card.dataset.stimLevel || '').toLowerCase();
      const formFactor = (card.dataset.formFactor || '').toLowerCase();

      for (const [group, values] of Object.entries(groups)) {
        if (!values.length) continue;
        const any = values.some(v => {
          const val = v.toLowerCase();

          if (group === 'collection') {
            if (val === 'all') {
              return true;
            } else {
              const collections = cardCollections.split(' ').filter(c => c.length > 0);
              return collections.includes(val);
            }
          } else if (group === 'goal') {
            return bestFor.includes(val);
          } else if (group === 'stim_level') {
            return stimLevel.includes(val);
          } else if (group === 'form_factor') {
            return formFactor.includes(val);
          } else if (group === 'brand') {
            return vendor.includes(val);
          } else if (group === 'size') {
            return title.includes(val) || tags.includes(val);
          } else if (group === 'color') {
            return title.includes(val) || tags.includes(val);
          } else if (group === 'material') {
            return title.includes(val) || tags.includes(val) || type.includes(val);
          } else if (group === 'style') {
            return title.includes(val) || tags.includes(val) || type.includes(val);
          } else {
            return vendor.includes(val) || tags.includes(val) || title.includes(val) || type.includes(val);
          }
        });
        if (!any) return false;
      }
      return true;
    };

    const applyFilters = () => {
      const groups = getActiveFilters();
      const hasFilters = Object.values(groups).some(arr => arr && arr.length);

      // Show/hide clear button based on active filters
      if (clearBtn) {
        clearBtn.style.display = hasFilters ? 'block' : 'none';
      }

      // Update toggle button to show active filter count
      if (this.filterToggle) {
        const count = Object.values(groups).reduce((sum, arr) => sum + arr.length, 0);
        const label = this.filterToggle.querySelector('span');
        if (label) {
          label.textContent = count > 0 ? `SORT & FILTER (${count})` : 'SORT & FILTER';
        }
      }

      this.productCards.forEach(card => {
        if (!hasFilters) {
          // No sidebar filters — still respect pill filter
          card.style.display = this.cardMatchesPill(card) ? '' : 'none';
        } else if (matchesFilters(card, groups)) {
          card.style.display = '';
        } else {
          card.style.display = 'none';
        }
      });
    };

    // Instant filter on checkbox change
    inputs.forEach(input => {
      input.addEventListener('change', applyFilters);
    });

    // Clear filters and reset sort
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        inputs.forEach(input => input.checked = false);
        sortInputs.forEach(input => {
          input.checked = input.value === 'best-selling';
        });
        this.sortProducts('best-selling');
        applyFilters();
      });
    }

    // Initial filter application
    setTimeout(applyFilters, 40);
  }

  initFullCardClickable() {
    const clickableCards = this.element.querySelectorAll('.product-card.full-card-clickable');
    
    clickableCards.forEach(card => {
      const productUrl = card.getAttribute('data-product-url');
      if (!productUrl) return;
      
      let isSliding = false;
      let startX = 0;
      let startY = 0;
      const slideThreshold = 10;
      
      const handleStart = (e) => {
        isSliding = false;
        const touch = e.touches ? e.touches[0] : e;
        startX = touch.clientX;
        startY = touch.clientY;
      };
      
      const handleMove = (e) => {
        if (!startX || !startY) return;
        
        const touch = e.touches ? e.touches[0] : e;
        const deltaX = Math.abs(touch.clientX - startX);
        const deltaY = Math.abs(touch.clientY - startY);
        
        if (deltaX > slideThreshold || deltaY > slideThreshold) {
          isSliding = true;
        }
      };
      
      const handleCardClick = (e) => {
        if (isSliding) return;
        
        const clickedElement = e.target;
        if (clickedElement.closest('a, button, input, select, textarea')) {
          return;
        }
        
        if (clickedElement.closest('.product-dots, .dot')) {
          return;
        }
        
        window.location.href = productUrl;
      };
      
      // Add event listeners
      card.addEventListener('mousedown', handleStart);
      card.addEventListener('mousemove', handleMove);
      card.addEventListener('click', handleCardClick);
      
      card.addEventListener('touchstart', handleStart, { passive: true });
      card.addEventListener('touchmove', handleMove, { passive: true });
      
      card.addEventListener('touchend', () => {
        setTimeout(() => { isSliding = false; }, 100);
      }, { passive: true });
      
      card.addEventListener('mouseup', () => {
        setTimeout(() => { isSliding = false; }, 100);
      });
    });
  }

  initPillFilterListener() {
    this.element.addEventListener('cpn:filter', (e) => {
      var tag = (e.detail && e.detail.tag) || '';
      this.activePillTag = tag;
      this.pillIsAll = tag === '';

      // Find no-results element (lives in pill nav section, outside grid)
      if (!this.noResultsEl) {
        this.noResultsEl = document.querySelector('.cpn-no-results');
      }

      this.applyPillFilter();
    });
  }

  applyPillFilter() {
    var visibleCount = 0;

    this.productCards.forEach(card => {
      if (this.pillIsAll) {
        card.style.display = '';
        visibleCount++;
      } else if (this.cardMatchesPill(card)) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });

    // Show/hide no results
    if (this.noResultsEl) {
      this.noResultsEl.style.display = visibleCount === 0 ? '' : 'none';
    }

    // Clear search when switching pills
    if (this.searchInput && this.searchInput.value.trim() !== '') {
      this.searchInput.value = '';
      if (this.searchClear) this.searchClear.style.display = 'none';
      this.element.classList.remove('searching');
    }
  }

  cardMatchesPill(card) {
    if (this.pillIsAll) return true;

    var pillTags = (card.getAttribute('data-pill-tags') || '').toLowerCase();
    if (!pillTags) return false;

    // Normalize filter tag: replace hyphens with spaces for matching
    var normalizedFilter = this.activePillTag.replace(/-/g, ' ').toLowerCase();
    var tags = pillTags.split(',');

    for (var i = 0; i < tags.length; i++) {
      var t = tags[i].trim();
      if (t === normalizedFilter || t === this.activePillTag.toLowerCase()) {
        return true;
      }
    }
    return false;
  }

  normalize(str) {
    return (str || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();
  }

  destroy() {
    // Clean up event listeners if needed
    clearTimeout(this.searchTimeout);
  }
}

// Auto-initialize components
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-component="collection-grid-page"]').forEach(element => {
    new CollectionGridPage(element);
  });
});

// Handle section reloads in theme editor
document.addEventListener('shopify:section:load', (event) => {
  const components = event.target.querySelectorAll('[data-component="collection-grid-page"]');
  components.forEach(element => {
    new CollectionGridPage(element);
  });
});
